/**
 * Slider mobile della sezione `lab-icon-columns` di 120''LAB (custom element `<lab-icon-columns-slider>`).
 *
 * Il markup e il CSS sono in `sections/lab-icon-columns.liquid`: sotto i 750px la lista
 * `[data-lab-ic-track]` scorre in orizzontale con scroll-snap (una card per volta). Questo modulo:
 * - sposta la lista di una card (larghezza della card + distanza) con i pulsanti precedente / successivo;
 * - disabilita "precedente" sulla prima card e "successivo" sull'ultima, leggendo lo scorrimento
 *   (anche quando l'utente scorre con il dito) e le variazioni di larghezza della lista;
 * - nasconde le frecce se le card entrano tutte nella lista;
 * - nell'editor del tema porta in vista la card del blocco selezionato.
 *
 * Da 750px le frecce sono nascoste dal CSS e la lista e una griglia: il modulo non fa nulla di visibile.
 */

/** Tolleranza in px per i valori frazionari di scrollLeft / larghezze. */
const TOLERANCE = 2;

class LabIconColumnsSlider extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #frame = 0;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLButtonElement | null} */
  #prev = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {HTMLElement | null} */
  #arrows = null;

  connectedCallback() {
    const track = this.querySelector('[data-lab-ic-track]');
    const prev = this.querySelector('[data-lab-ic-prev]');
    const next = this.querySelector('[data-lab-ic-next]');
    if (!(track instanceof HTMLElement) || !(prev instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) {
      return;
    }

    this.#track = track;
    this.#prev = prev;
    this.#next = next;
    this.#arrows = prev.parentElement;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    track.addEventListener('scroll', this.#scheduleUpdate, { passive: true, signal });
    prev.addEventListener('click', () => this.#move(-1), { signal });
    next.addEventListener('click', () => this.#move(1), { signal });
    this.addEventListener('shopify:block:select', this.#showSelectedBlock, { signal });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(this.#scheduleUpdate);
      this.#resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', this.#scheduleUpdate, { passive: true, signal });
    }

    // Passando da mobile a desktop (e viceversa) la lista cambia da slider a griglia.
    window.matchMedia('(max-width: 749px)').addEventListener('change', this.#scheduleUpdate, { signal });

    this.#update();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    if (this.#frame) {
      cancelAnimationFrame(this.#frame);
      this.#frame = 0;
    }

    this.#track = null;
    this.#prev = null;
    this.#next = null;
    this.#arrows = null;
  }

  /**
   * Sposta la lista di una card. In RTL `scrollLeft` e negativo: il verso si inverte.
   * @param {1 | -1} direction 1 = card successiva, -1 = card precedente
   */
  #move(direction) {
    const track = this.#track;
    const slide = track?.firstElementChild;
    if (!track || !slide) return;

    const style = getComputedStyle(track);
    const gap = parseFloat(style.columnGap) || 0;
    const step = slide.getBoundingClientRect().width + gap;
    const sign = style.direction === 'rtl' ? -direction : direction;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollBy({ left: sign * step, behavior: reducedMotion ? 'auto' : 'smooth' });
  }

  #scheduleUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const track = this.#track;
    const prev = this.#prev;
    const next = this.#next;
    if (!track || !prev || !next) return;

    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);

    if (this.#arrows) this.#arrows.hidden = max <= TOLERANCE;

    this.#setDisabled(prev, next, position <= TOLERANCE);
    this.#setDisabled(next, prev, position >= max - TOLERANCE);
  }

  /**
   * Imposta `disabled` su un pulsante; se aveva il focus lo passa all'altro pulsante,
   * cosi chi naviga da tastiera non lo perde arrivando alla prima o all'ultima card.
   * @param {HTMLButtonElement} button
   * @param {HTMLButtonElement} other
   * @param {boolean} disabled
   */
  #setDisabled(button, other, disabled) {
    if (disabled && !button.disabled && document.activeElement === button && !other.disabled) {
      other.focus();
    }

    button.disabled = disabled;
  }

  /**
   * Nell'editor del tema, selezionando un blocco la sua card viene portata in vista.
   * @param {Event} event
   */
  #showSelectedBlock = (event) => {
    const track = this.#track;
    const target = event.target;
    if (!track || !(target instanceof Element)) return;
    if (track.scrollWidth - track.clientWidth <= TOLERANCE) return;

    target.closest('.lab-icon-columns__slide')?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
  };
}

if (!customElements.get('lab-icon-columns-slider')) {
  customElements.define('lab-icon-columns-slider', LabIconColumnsSlider);
}
